<template>
  <section id="main-container" style="background-color: rgb(230, 230, 230);">
    <v-container fluid>
      <v-layout flex>
        <v-flex xs12 style="max-width:640px;margin:0 auto;">
          <div id="centered-content" class="pa-4">
            <img
              height="120px"
              style="margin:0 auto; display:block; padding-bottom:24px; "
              src="../assets/login-cb-logo.svg"
              alt=""
            />
            <h1 class="title mb-3">Authorize application</h1>

            <v-alert v-if="paramError" type="error" :value="true" class="mb-3">
              This link is missing required authorization parameters. Start the connection again from your client.
            </v-alert>

            <v-alert v-if="errorMessage" type="error" :value="true" class="mb-3">
              {{ errorMessage }}
            </v-alert>

            <v-card v-if="!paramError && sessionReady" class="pa-4">
              <p class="body-1">
                An application is requesting permission to access Caseblocks on your behalf.
              </p>
              <p class="body-2 grey--text text--darken-1 mb-2">
                <strong>Client</strong><br />
                <span class="break-all">{{ clientLabel }}</span>
              </p>
              <p class="body-2 grey--text text--darken-1 mb-4">
                <strong>Return to</strong><br />
                <span class="break-all">{{ redirectHost }}</span>
              </p>
              <v-btn color="primary" block :loading="submitting" class="mb-2" @click="approve">
                Allow access
              </v-btn>
              <v-btn block flat @click="deny" :disabled="submitting">
                Deny
              </v-btn>
            </v-card>
          </div>
        </v-flex>
      </v-layout>
    </v-container>
  </section>
</template>

<script>
import cfg from "@/cfg.js"
import Helpers from "@/helpers.js"

export default {
  name: "OAuthAuthorize",
  data() {
    return {
      paramError: false,
      sessionReady: false,
      submitting: false,
      errorMessage: "",
    }
  },
  computed: {
    clientLabel() {
      const id = this.$route.query.client_id
      return id || "—"
    },
    redirectHost() {
      const uri = this.$route.query.redirect_uri
      if (!uri || typeof uri !== "string") return "—"
      try {
        return new URL(uri).origin
      } catch (e) {
        return uri
      }
    },
  },
  watch: {
    $route() {
      this.bootstrap()
    },
  },
  mounted() {
    this.bootstrap()
  },
  methods: {
    hasRequiredParams() {
      const q = this.$route.query
      const rt = q.response_type
      const responseOk = !rt || rt === "code"
      const requiredParamsPresent = (
        q.client_id &&
        q.redirect_uri &&
        q.code_challenge &&
        q.code_challenge_method === "S256" &&
        q.resource &&
        responseOk
      )
      console.log("[OAuthDebug][OAuthAuthorize.hasRequiredParams]", {
        fullPath: this.$route.fullPath,
        response_type: rt,
        code_challenge_method: q.code_challenge_method,
        hasClientId: !!q.client_id,
        hasRedirectUri: !!q.redirect_uri,
        hasCodeChallenge: !!q.code_challenge,
        hasResource: !!q.resource,
        responseTypeValid: responseOk,
        valid: !!requiredParamsPresent,
      })
      return requiredParamsPresent
    },
    bootstrap() {
      console.log("[OAuthDebug][OAuthAuthorize.bootstrap] start", {
        href: typeof window !== "undefined" ? window.location.href : null,
        path: this.$route.path,
        fullPath: this.$route.fullPath,
        queryKeys: Object.keys(this.$route.query || {}),
      })
      this.errorMessage = ""
      this.sessionReady = false
      if (!this.hasRequiredParams()) {
        console.warn("[OAuthDebug][OAuthAuthorize.bootstrap] missing required oauth params", {
          query: this.$route.query,
        })
        this.paramError = true
        return
      }
      this.paramError = false
      const token = window.localStorage.getItem("session")
      const jwtValid = Helpers.isJwtValid(token)
      console.log("[OAuthDebug][OAuthAuthorize.bootstrap] session check", {
        hasToken: !!token,
        jwtValid,
      })
      if (!jwtValid) {
        // Do not encodeURIComponent(fullPath): Vue Router encodes query values once;
        // double-encoding breaks decode and post-login router.push(next) lands on / or wrong route.
        console.warn("[OAuthDebug][OAuthAuthorize.bootstrap] invalid session, redirecting to /login with next", {
          next: this.$route.fullPath,
        })
        this.$router.replace({
          path: "/login",
          query: { next: this.$route.fullPath },
        })
        return
      }
      if (Helpers.isJwtMfaPending(token)) {
        console.warn("[OAuthDebug][OAuthAuthorize.bootstrap] MFA pending, redirecting to /login/mfa with next", {
          next: this.$route.fullPath,
        })
        this.$router.replace({
          path: "/login/mfa",
          query: { next: this.$route.fullPath },
        })
        return
      }
      this.sessionReady = true
      console.log("[OAuthDebug][OAuthAuthorize.bootstrap] ready to render authorize card")
    },
    approve() {
      const q = this.$route.query
      const token = window.localStorage.getItem("session")
      this.submitting = true
      this.errorMessage = ""
      const base = cfg.apiEndPoint()
      const payload = {
        client_id: q.client_id,
        redirect_uri: q.redirect_uri,
        state: q.state || "",
        code_challenge: q.code_challenge,
        code_challenge_method: q.code_challenge_method,
        resource: q.resource,
        scope: q.scope || "mcp",
      }
      console.log("[OAuthDebug][OAuthAuthorize.approve] submitting", {
        endpoint: base.replace(/\/$/, "") + "/oauth/approve",
        payload,
        hasToken: !!token,
      })
      fetch(base.replace(/\/$/, "") + "/oauth/approve", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify(payload),
      })
        .then((res) => res.json().then((data) => ({ ok: res.ok, status: res.status, data })))
        .then(({ ok, status, data }) => {
          this.submitting = false
          console.log("[OAuthDebug][OAuthAuthorize.approve] response", {
            ok,
            status,
            hasRedirectUrl: !!(data && data.redirect_url),
            data,
          })
          if (ok && data.redirect_url) {
            window.location.href = data.redirect_url
            return
          }
          const msg =
            (data && data.error_description) ||
            (data && data.error) ||
            "Authorization failed. Try again or contact support."
          this.errorMessage = msg
        })
        .catch((error) => {
          this.submitting = false
          console.error("[OAuthDebug][OAuthAuthorize.approve] network error", error)
          this.errorMessage = "Network error. Check your connection and try again."
        })
    },
    deny() {
      const q = this.$route.query
      const raw = q.redirect_uri
      console.log("[OAuthDebug][OAuthAuthorize.deny] start", {
        hasRedirectUri: !!raw,
        statePresent: !!q.state,
      })
      if (!raw || typeof raw !== "string") {
        this.errorMessage = "Invalid redirect — cannot deny safely."
        return
      }
      try {
        const u = new URL(raw)
        u.searchParams.set("error", "access_denied")
        u.searchParams.set("error_description", "User denied access")
        const st = q.state
        if (st && typeof st === "string") {
          u.searchParams.set("state", st)
        }
        console.log("[OAuthDebug][OAuthAuthorize.deny] redirecting to deny URL", {
          redirectUrl: u.toString(),
        })
        window.location.href = u.toString()
      } catch (e) {
        console.error("[OAuthDebug][OAuthAuthorize.deny] invalid redirect URI", {
          redirect_uri: raw,
          error: e && e.message ? e.message : e,
        })
        this.errorMessage = "Invalid redirect URI."
      }
    },
  },
}
</script>

<style scoped>
.break-all {
  word-break: break-all;
}
#main-container {
  min-height: 100vh;
  display: flex;
  -webkit-box-flex: 1;
  -ms-flex: 1 1 auto;
  flex: 1 1 auto;
}
</style>
